/**
 * @file   src\components\Inventory\EditInventory\InventoryBudget.js
 * @brief  This file is responsible for the view of inventory budget section.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { removeNegativeInput } from "../../../helpers/validations";
import TextBox from "../../Common/TextBox";
import { checkInventoryType } from "../../../helpers/inventory";
import {
  INVENTORY_ALL_TYPE,
  INVENTORY_LTR,
  INVENTORY_LTR_STR,
  INVENTORY_SELL,
  INVENTORY_SELL_LTR,
  INVENTORY_SELL_STR,
  INVENTORY_STR,
  SELL_ASKING_PRICE,
  LTR_ASKING_PRICE,
  STR_ASKING_PRICE,
} from "../../../constants/common";
import { replaceComma, thousandSeperatedValue } from "../../../helpers/common";

const InventoryBudget = (props) => {
  const {
    sellAskngPrice,
    LTRAskngPrce,
    STRAskngPrce,
    propertyPurpose,
    handleFormValues,
    askingPriceError,
    shortTermPriceError,
    longTermPriceError,
    zestimate,
    disabled = false
  } = props;

  // Initialize component state variables and constants.
  const invType = props?.invType || checkInventoryType(propertyPurpose);

  const isSellOnly = invType === INVENTORY_SELL;

  const isLTROnly = invType === INVENTORY_LTR;
  const isSTROnly = invType === INVENTORY_STR;
  const isSell_And_LTR = invType === INVENTORY_SELL_LTR;
  const isSell_And_STR = invType === INVENTORY_SELL_STR;
  const isSTR_And_LTR = invType === INVENTORY_LTR_STR;
  const isAllType = invType === INVENTORY_ALL_TYPE;
  let askingPrice = sellAskngPrice;
  const [sellCursorPos, setSellCursorPos] = useState(0);
  const [lTRCursorPos, setLTRCursorPos] = useState(0);
  const [sTRCursorPos, setSTRCursorPos] = useState(0);

  if (isSTROnly) {
    askingPrice = STRAskngPrce;
  } else if (isLTROnly) {
    askingPrice = LTRAskngPrce;
  }

  // References
  const sellRef = useRef();
  const ltrRef = useRef();
  const strRef = useRef();

  // sellCursorPos state change side effect handling.
  useEffect(() => {
    if (sellRef.current) {
      sellRef.current.setSelectionRange(sellCursorPos, sellCursorPos);
    }
  }, [sellCursorPos]);

  // lTRCursorPos state change side effect handling.
  useEffect(() => {
    if (ltrRef.current) {
      ltrRef.current.setSelectionRange(lTRCursorPos, lTRCursorPos);
    }
  }, [lTRCursorPos]);

  // sTRCursorPos state change side effect handling.
  useEffect(() => {
    if (strRef.current) {
      strRef.current.setSelectionRange(sTRCursorPos, sTRCursorPos);
    }
  }, [sTRCursorPos]);

  // To handle the asking price change
  const handleAskngPriceChange = (e, section) => {
    let value = removeNegativeInput(e.target.value);
    if (value === "0") {
      value = 1;
    }
    switch (section) {
      case SELL_ASKING_PRICE:
        if (isLTROnly) {
          handleFormValues({ [LTR_ASKING_PRICE]: replaceComma(value) });
          handleFormValues({ [LTR_ASKING_PRICE]: replaceComma(value) });

          if (
            thousandSeperatedValue(LTRAskngPrce)?.length + 1 ==
            thousandSeperatedValue(e.target.value)?.length
          ) {
            setSellCursorPos(e.target.selectionStart);
          } else if (
            thousandSeperatedValue(LTRAskngPrce)?.length + 2 ==
            thousandSeperatedValue(e.target.value)?.length
          ) {
            setSellCursorPos(e.target.selectionStart + 1);
          } else {
            setSellCursorPos(e.target.selectionStart);
          }
        } else if (isSTROnly) {
          handleFormValues({ [STR_ASKING_PRICE]: replaceComma(value) });
          if (
            thousandSeperatedValue(STRAskngPrce)?.length + 1 ==
            thousandSeperatedValue(e.target.value)?.length
          ) {
            setSellCursorPos(e.target.selectionStart);
          } else if (
            thousandSeperatedValue(STRAskngPrce)?.length + 2 ==
            thousandSeperatedValue(e.target.value)?.length
          ) {
            setSellCursorPos(e.target.selectionStart + 1);
          } else {
            setSellCursorPos(e.target.selectionStart);
          }
        } else {
          handleFormValues({ [SELL_ASKING_PRICE]: replaceComma(value) });
          if (
            thousandSeperatedValue(sellAskngPrice)?.length + 1 ==
            thousandSeperatedValue(e.target.value)?.length
          ) {
            setSellCursorPos(e.target.selectionStart);
          } else if (
            thousandSeperatedValue(sellAskngPrice)?.length + 2 ==
            thousandSeperatedValue(e.target.value)?.length
          ) {
            setSellCursorPos(e.target.selectionStart + 1);
          } else {
            setSellCursorPos(e.target.selectionStart);
          }
        }
        break;
      case LTR_ASKING_PRICE:
        handleFormValues({ [LTR_ASKING_PRICE]: replaceComma(value) });
        if (
          thousandSeperatedValue(LTRAskngPrce)?.length + 1 ==
          thousandSeperatedValue(e.target.value)?.length
        ) {
          setLTRCursorPos(e.target.selectionStart);
        } else if (
          thousandSeperatedValue(LTRAskngPrce)?.length + 2 ==
          thousandSeperatedValue(e.target.value)?.length
        ) {
          setLTRCursorPos(e.target.selectionStart + 1);
        } else {
          setLTRCursorPos(e.target.selectionStart);
        }
        break;
      case STR_ASKING_PRICE:
        handleFormValues({ [STR_ASKING_PRICE]: replaceComma(value) });
        if (
          thousandSeperatedValue(STRAskngPrce)?.length + 1 ==
          thousandSeperatedValue(e.target.value)?.length
        ) {
          setSTRCursorPos(e.target.selectionStart);
        } else if (
          thousandSeperatedValue(STRAskngPrce)?.length + 2 ==
          thousandSeperatedValue(e.target.value)?.length
        ) {
          setSTRCursorPos(e.target.selectionStart + 1);
        } else {
          setSTRCursorPos(e.target.selectionStart);
        }
        break;
    }
  };

  return (
    <div className="wantad-location-wrap mb-3">
      <div className="wantad-timing-wrap pb-0">
        {(isSellOnly ||
          isAllType ||
          isSell_And_LTR ||
          isSell_And_STR ||
          isLTROnly ||
          isSTROnly) && (
            <>
              <div className="land-radio-wrap position-relative pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="common-input-wrap mb-4 position-relative">
                      <div className="innerdollar-sign-icon-first">$</div>
                      <Form.Label
                        className={
                          askingPriceError ? "mb-2 red-marked-label" : "mb-2"
                        }
                      >
                        {isAllType || isSell_And_LTR || isSell_And_STR
                          ? "Selling Asking Price"
                          : isLTROnly || isSTROnly
                            ? "Asking Price/Month"
                            : "Asking Price"}

                        <span>*</span>
                      </Form.Label>

                        <TextBox
                          className={
                            askingPriceError
                              ? "form-control pad-left-25 red-marked-formcontrol"
                              : "form-control pad-left-25 "
                          }
                          name="askingPrice"
                          type="text"
                          value={
                            askingPrice ? thousandSeperatedValue(askingPrice) : ""
                          }
                          placeholder="Asking Price"
                          onChange={(e) => {
                            handleAskngPriceChange(e, SELL_ASKING_PRICE);
                          }}
                          txtRef={sellRef}
                          disabled={disabled}
                        />

                      {zestimate && (
                        <small className="form-text text-muted">
                          Zestimate: ${thousandSeperatedValue(zestimate)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

        {(isAllType || isSell_And_LTR || isSTR_And_LTR) && (
          <>
            <div className="land-radio-wrap position-relative pb-0">
              <div className="row">
                <div className="col-md-12">
                  <div className="common-input-wrap mb-2 position-relative">
                    <div
                      className={
                        longTermPriceError
                          ? "innerdollar-sign-icon-second red-marked-sign"
                          : "innerdollar-sign-icon-second "
                      }
                    >
                      $
                    </div>
                    <Form.Label
                      className={
                        longTermPriceError ? "mb-2 red-marked-label" : "mb-2"
                      }
                    >
                      Long-term Renting Asking Price (Monthly) <span>*</span>
                    </Form.Label>

                    <TextBox
                      className={
                        longTermPriceError
                          ? "form-control pad-left-25 red-marked-formcontrol"
                          : "form-control pad-left-25 "
                      }
                      name="LTRaskingPrice"
                      type="text"
                      value={
                        LTRAskngPrce ? thousandSeperatedValue(LTRAskngPrce) : ""
                      }
                      placeholder="Long-term Renting Asking Price"
                      onChange={(e) => {
                        handleAskngPriceChange(e, LTR_ASKING_PRICE);
                      }}
                      txtRef={ltrRef}
                      disabled={disabled}
                    />
                    <div className="error-msg"></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {(isAllType || isSell_And_STR || isSTR_And_LTR) && (
          <>
            <div className="land-radio-wrap position-relative pb-0">
              <div className="row">
                <div className="col-md-12">
                  <div className="common-input-wrap mb-4 position-relative">
                    <div
                      className={
                        shortTermPriceError
                          ? "innerdollar-sign-icon-second red-marked-sign"
                          : "innerdollar-sign-icon-second "
                      }
                    >
                      $
                    </div>
                    <Form.Label
                      className={
                        shortTermPriceError ? "mb-2 red-marked-label" : "mb-2"
                      }
                    >
                      Short-term Renting Asking Price (Nightly) <span>*</span>
                    </Form.Label>
                    <TextBox
                      className={
                        shortTermPriceError
                          ? "form-control pad-left-25 red-marked-formcontrol"
                          : "form-control pad-left-25"
                      }
                      name="LTRaskingPrice"
                      type="text"
                      value={
                        STRAskngPrce ? thousandSeperatedValue(STRAskngPrce) : ""
                      }
                      placeholder="Short-term Renting Asking Price"
                      onChange={(e) => {
                        handleAskngPriceChange(e, STR_ASKING_PRICE);
                      }}
                      txtRef={strRef}
                      disabled={disabled}
                    />
                    <div className="error-msg"></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InventoryBudget;
